import { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'
import {
  Home,
  Wrench,
  Settings,
  FileText,
  Hash,
  ChevronDown,
  ChevronRight,
  Crown,
  Menu,
  X,
  Sparkles,
  Globe,
  Lock,
  Code,
  Shield,
  Star,
  Send,
  ShoppingBag,
  ExternalLink,
  ScrollText,
  Heart,
} from 'lucide-react'
import { Button } from '@/components/ui'
import { useFavorites } from '@/hooks/useFavorites'
import { tools } from '@/pages/Tools'
import { CONTACT } from '@/config/contact'

interface NavItem {
  title: string
  href?: string
  icon: React.ComponentType<{ className?: string }>
  children?: { title: string; href: string }[]
  adminOnly?: boolean
  authOnly?: boolean
  external?: boolean
}

const baseNavigation: NavItem[] = [
  { title: 'Dashboard', href: '/', icon: Home },
  { title: 'All Tools', href: '/tools', icon: Wrench },
  {
    title: 'Special Tools',
    icon: Sparkles,
    children: [
      { title: 'Temp Mail', href: '/temp-mail' },
      { title: 'Credit Card Gen', href: '/credit-card-generator' },
      { title: 'Identity Gen', href: '/identity' },
      { title: 'cURL Converter', href: '/curl-converter' },
      { title: 'IP Information', href: '/ip-info' },
      { title: 'Luhn Validator', href: '/luhn-validator' },
    ],
  },
  {
    title: 'Developer Tools',
    icon: Code,
    children: [
      { title: 'JSON Formatter', href: '/json-formatter' },
      { title: 'Timestamp', href: '/timestamp' },
      { title: 'Hash Generator', href: '/hash-generator' },
      { title: 'UUID Generator', href: '/uuid-generator' },
      { title: 'URL Encoder/Decoder', href: '/url-encoder-decoder' },
      { title: 'Unit Converter', href: '/unit-converter' },
      { title: 'Pomodoro Timer', href: '/pomodoro-timer' },
      { title: 'ASCII Art Generator', href: '/ascii-art-generator' },
      { title: 'Text Diff', href: '/text-diff' },
      { title: 'Text Diff (Side-by-Side)', href: '/text-diff-side-by-side' },
      { title: 'String Inspector', href: '/string-inspector' },
      { title: 'CRON Job Editor', href: '/cron-editor' },
    ],
  },
  {
    title: 'Encryptors',
    icon: Lock,
    children: [
      { title: 'Adyen v1', href: '/encryptor/adyen-v1' },
      { title: 'Adyen v2', href: '/encryptor/adyen-v2' },
      { title: 'Cybersource v1', href: '/encryptor/cybersource-v1' },
      { title: 'Cybersource v2', href: '/encryptor/cybersource-v2' },
      { title: 'CARD/CVV', href: '/encryptor/card-cvv' },
      { title: 'Vantiv', href: '/encryptor/vantiv' },
      { title: 'Clover', href: '/encryptor/clover' },
      { title: 'Eway', href: '/encryptor/eway' },
      { title: 'CardConnect', href: '/encryptor/cardconnect' },
      { title: 'Paytrace', href: '/encryptor/paytrace' },
      { title: 'Zuora', href: '/encryptor/zuora' },
      { title: 'FirstData v2', href: '/encryptor/firstdata-v2' },
      { title: 'SecurePay', href: '/encryptor/securepay' },
      { title: 'Auruspay', href: '/encryptor/auruspay' },
      { title: 'Oceanpayment', href: '/encryptor/oceanpayment' },
      { title: 'Captcha v3', href: '/encryptor/captcha-v3' },
      { title: 'Freedompay', href: '/encryptor/freedompay' },
      { title: 'Paysimple', href: '/encryptor/paysimple' },
      { title: 'BINLIST', href: '/encryptor/binlist' },
    ],
  },
  {
    title: 'Documents',
    icon: FileText,
    children: [
      { title: 'PDF Studio', href: '/pdf-studio' },
      { title: 'Student Slip', href: '/student-slip' },
      { title: 'Resume Builder', href: '/resume-builder' },
      { title: 'Invoice Generator', href: '/invoice-generator' },
      { title: 'Markdown Editor', href: '/markdown-editor' },
    ],
  },
  {
    title: 'Utilities',
    icon: Hash,
    children: [
      { title: 'QR Generator', href: '/qr-generator' },
      { title: 'Password Gen', href: '/password-generator' },
      { title: 'Image Compressor', href: '/image-compressor' },
      { title: 'Color Picker', href: '/color-picker' },
      { title: 'URL Shortener', href: '/url-shortener' },
      { title: 'E-Signature', href: '/e-signature' },
      { title: 'Video Downloader', href: '/video-downloader' },
      { title: 'Word Counter', href: '/word-counter' },
    ],
  },
  {
    title: 'SEO',
    icon: Globe,
    children: [
      { title: 'Meta Tags', href: '/meta-tags' },
      { title: 'Sitemap Generator', href: '/sitemap-generator' },
    ],
  },
  { title: 'Telegram Portal', href: CONTACT.telegram.portal, icon: Send, external: true },
  { title: 'Shop', href: '/shop', icon: ShoppingBag },
  { title: 'Admin Panel', href: '/admin', icon: Shield, adminOnly: true },
  { title: 'VIP', href: '/vip-pricing', icon: Crown },
  { title: 'Settings', href: '/settings', icon: Settings, authOnly: true },
  { title: 'Donate', href: '/donate', icon: Heart },
  { title: 'Terms of Service', href: '/tos', icon: ScrollText },
]

export function Sidebar() {
  const location = useLocation()
  const { favorites, toggleFavorite } = useFavorites()
  const { isAdmin, isAuthenticated } = useAuth()
  const [expanded, setExpanded] = useState<string[]>(['Favorites'])
  const [mobileOpen, setMobileOpen] = useState(false)
  const scrollPositions = useRef<Record<string, number>>({})
  const submenuRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const navRef = useRef<HTMLElement>(null)

  const [navigation, setNavigation] = useState<NavItem[]>([])

  useEffect(() => {
    const favoriteTools = tools.filter(tool => favorites.includes(tool.href))

    const filteredBase = baseNavigation.filter(item => {
      if (item.adminOnly && !isAdmin) return false
      if (item.authOnly && !isAuthenticated) return false
      if (item.title === 'VIP') return false // Hide VIP pricing
      return true
    })

    const newNavigation: NavItem[] = [
      ...filteredBase.slice(0, 2),
      // Conditionally add Favorites category
      ...(favoriteTools.length > 0 ? [{
        title: 'Favorites',
        icon: Star,
        children: favoriteTools.map(tool => ({ title: tool.title, href: tool.href })),
      }] : []),
      ...filteredBase.slice(2).map(item => {
        if (item.children) {
          // Filter out children (tools) that are favorited
          const filteredChildren = item.children.filter(child => !favorites.includes(child.href));
          return { ...item, children: filteredChildren };
        }
        return item;
      }),
    ]
    setNavigation(newNavigation)
  }, [favorites, isAdmin, isAuthenticated])

  // Restore scroll position after navigation
  useEffect(() => {
    // Use multiple timeouts to ensure DOM is fully updated after navigation
    const timers = [
      setTimeout(() => {
        Object.entries(submenuRefs.current).forEach(([title, el]) => {
          if (el && scrollPositions.current[title] !== undefined) {
            el.scrollTop = scrollPositions.current[title]
          }
        })
      }, 0),
      setTimeout(() => {
        Object.entries(submenuRefs.current).forEach(([title, el]) => {
          if (el && scrollPositions.current[title] !== undefined) {
            el.scrollTop = scrollPositions.current[title]
          }
        })
      }, 50),
    ]

    return () => timers.forEach(t => clearTimeout(t))
  }, [location.pathname, navigation])

  // Save scroll positions continuously
  useEffect(() => {
    const cleanupFunctions: Array<() => void> = []

    // Attach scroll listeners to all current submenus
    Object.entries(submenuRefs.current).forEach(([title, el]) => {
      if (el) {
        const handler = () => {
          scrollPositions.current[title] = el.scrollTop
        }
        el.addEventListener('scroll', handler, { passive: true })
        cleanupFunctions.push(() => el.removeEventListener('scroll', handler))
      }
    })

    return () => cleanupFunctions.forEach(cleanup => cleanup())
  })

  // Also save scroll positions periodically (for non-scroll events)
  useEffect(() => {
    const interval = setInterval(() => {
      Object.entries(submenuRefs.current).forEach(([title, el]) => {
        if (el) {
          scrollPositions.current[title] = el.scrollTop
        }
      })
    }, 500)

    return () => clearInterval(interval)
  })

  const toggleExpand = (e: React.MouseEvent<HTMLButtonElement>, title: string) => {
    e.preventDefault()
    // Save nav scroll position before state change triggers re-render
    const navScroll = navRef.current?.scrollTop ?? 0

    // Save all submenu scroll positions before expansion change
    Object.entries(submenuRefs.current).forEach(([title, el]) => {
      if (el) {
        scrollPositions.current[title] = el.scrollTop
      }
    })

    setExpanded((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    )
    // Restore after React re-renders
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (navRef.current) navRef.current.scrollTop = navScroll
        // Also restore submenu scroll positions
        Object.entries(submenuRefs.current).forEach(([title, el]) => {
          if (el && scrollPositions.current[title] !== undefined) {
            el.scrollTop = scrollPositions.current[title]
          }
        })
      })
    })
  }

  const isActive = (href: string) => location.pathname === href

  const handleLinkClick = (title: string) => {
    // Save scroll position before navigation
    const el = submenuRefs.current[title]
    if (el) {
      scrollPositions.current[title] = el.scrollTop
    }
    setMobileOpen(false)
  }

  const NavLink = ({ item }: { item: NavItem }) => {
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expanded.includes(item.title)
    const Icon = item.icon

    if (hasChildren) {
      return (
        <div>
          <div className="flex items-center justify-between">
            <button
              onClick={(e) => toggleExpand(e, item.title)}
              className={cn(
                'flex-1 flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors',
                'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              <span className="flex items-center gap-3">
                <Icon className={cn(
                  'h-4 w-4',
                  item.title === 'Favorites' ? 'text-yellow-500' :
                  item.title === 'Encryptors' ? 'text-orange-500' :
                  'text-blue-500'
                )} />
                {item.title}
              </span>
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          </div>
          {isExpanded && (
            <div
              ref={(el) => { submenuRefs.current[item.title] = el }}
              className="ml-4 mt-1 space-y-1 border-l border-border pl-3 max-h-[300px] overflow-y-auto custom-scrollbar"
            >
              {item.children!.map((child) => {
                const tool = tools.find(t => t.href === child.href)
                const showLock = tool?.authOnly && !isAuthenticated
                return (
                  <div key={child.href} className="flex items-center justify-between">
                    <Link
                      to={child.href}
                      onClick={() => handleLinkClick(item.title)}
                      className={cn(
                        'block w-full rounded-md px-3 py-2 text-sm transition-colors',
                        isActive(child.href)
                          ? 'bg-primary/10 text-primary glow-text'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      )}
                    >
                      <span className="flex items-center gap-2">
                        {child.title}
                      </span>
                    </Link>
                    {showLock ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled
                      >
                        <Lock className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => toggleFavorite(child.href)}
                        disabled={!isAuthenticated}
                        title={!isAuthenticated ? 'Login to favorite tools' : ''}
                      >
                        <Star className={`h-4 w-4 ${favorites.includes(child.href) ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`} />
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )
    }

    if (item.external) {
      return (
        <a
          href={item.href!}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setMobileOpen(false)}
          className={cn(
            'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
            'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
          )}
        >
          <Icon className={cn('h-4 w-4',
            item.title === 'Telegram Portal' && 'text-[#0088cc]'
          )} />
          {item.title}
          <ExternalLink className="h-3 w-3 ml-auto opacity-50" />
        </a>
      )
    }

    return (
      <Link
        to={item.href!}
        onClick={() => setMobileOpen(false)}
        className={cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          isActive(item.href!)
            ? 'bg-primary/10 text-primary glow-text'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          item.adminOnly && 'text-red-400 hover:text-red-300'
        )}
      >
        <Icon className={cn('h-4 w-4', item.adminOnly && 'text-red-400', item.title === 'Donate' && 'text-pink-500')} />
        {item.title}
      </Link>
    )
  }

  return (
    <>
      {/* Mobile toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed left-4 top-4 z-50 md:hidden"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </Button>

      {/* Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen w-64 border-r border-border bg-card transition-transform md:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center border-b border-border px-6">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="/images/logo-48.png"
                alt="SCB|PH Logo"
                className="h-8 w-8"
              />
              <span className="text-lg font-bold text-primary glow-text">
                SCB|PH
              </span>
            </Link>
          </div>

          {/* Navigation */}
          <nav ref={navRef} className="flex-1 space-y-1 overflow-y-auto p-4 custom-scrollbar">
            {navigation.map((item) => (
              <NavLink key={item.title} item={item} />
            ))}
          </nav>

          {/* Footer */}
          <div className="border-t border-border p-4">
            <div className="flex flex-col items-center justify-center gap-1">
              <p className="text-xs text-muted-foreground">
                <span className="text-primary">{'>'}</span> Terminal v3.5
              </p>
              <Link to="/changelog" className="text-xs text-muted-foreground hover:underline">
                View Changelog
              </Link>
            </div>
          </div>
        </div>
      </aside>
    </>
  )
}
